// src/components/DiagnosticResult.tsx
import React, { useState } from 'react';
import RadarChart from './RadarChart';
import { DimensionScores, ReadinessBand, Recommendation } from '../lib/quizState';
import { AgentRead, getAgentRead, submitDiagnosticIntake } from '../lib/agentApi';
import { QUIZ_QUESTIONS } from '../data/quizQuestions';

interface DiagnosticResultProps {
  scores: DimensionScores;
  band: ReadinessBand;
  recommendation: Recommendation;
  answers: Record<string, number>;
  onReset: () => void;
  onContactClick: () => void;
}

const DiagnosticResult: React.FC<DiagnosticResultProps> = ({
  scores, band, recommendation, answers, onReset, onContactClick
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [callTriggered, setCallTriggered] = useState(false);
  const [read, setRead] = useState<AgentRead | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError('Enter your name.');
    if (!phone.replace(/\D/g, '').match(/^\d{10,15}$/)) return setError('Enter a valid phone number.');
    if (!email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) return setError('Enter a valid email.');

    setLoading(true);
    setError(null);
    const answerObjects = QUIZ_QUESTIONS
      .filter(q => answers[q.id] !== undefined)
      .map(q => ({ question: q.question, answer: q.answers[answers[q.id]].label }));

    // Score the assessment first so we have the agenda to send with the intake
    const agentRead = await getAgentRead({ scores, band, recommendation, answers: answerObjects, email });

    // Await the webhook ack so the UI only promises a call that was triggered
    const triggered = await submitDiagnosticIntake({
      name,
      phone,
      email,
      company,
      scores,
      band,
      recommendation,
      agenda: agentRead.agenda,
      answers: answerObjects,
    });

    setCallTriggered(triggered);
    setRead(agentRead);
    setSubmitted(true);
    setLoading(false);
  };

  const bandClass =
    band === 'Strong' ? 'text-success-emerald' :
    band === 'Capable' ? 'text-command-amber' :
    band === 'Mixed' ? 'text-warning-amber' :
    'text-danger-red';

  return (
    <article className="max-w-4xl mx-auto">
      <div className="mb-12">
        <div className="eyebrow eyebrow-accent mb-4">Your readiness report</div>
        <h2 className={`text-5xl md:text-6xl font-bold mb-6 tracking-tight ${bandClass}`}>
          {band}
        </h2>
        <p className="text-lg text-steel measure">{recommendation.reason}</p>
      </div>

      <div className="mb-12">
        <RadarChart scores={scores} />
      </div>

      {!submitted && (
        <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-8">
          <div className="mb-6">
            <div className="eyebrow mb-3">
              Start AI Readiness Assessment + a call from us
            </div>
            <p className="text-steel text-sm">
              Our AI agent will call you within minutes to set up a time to meet.
              Your full readiness report appears on this page when you submit.
            </p>
          </div>

          <div className="space-y-3">
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name"
              required
              className="w-full bg-void-black border border-grid-line text-signal-white px-4 py-3 font-mono text-sm focus:border-command-amber"
            />
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="Phone (we will call you)"
              required
              className="w-full bg-void-black border border-grid-line text-signal-white px-4 py-3 font-mono text-sm focus:border-command-amber"
            />
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="w-full bg-void-black border border-grid-line text-signal-white px-4 py-3 font-mono text-sm focus:border-command-amber"
            />
            <input
              type="text"
              value={company}
              onChange={e => setCompany(e.target.value)}
              placeholder="Company (optional)"
              className="w-full bg-void-black border border-grid-line text-signal-white px-4 py-3 font-mono text-sm focus:border-command-amber"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-block"
            >
              {loading ? 'Sending…' : 'Call me now'}
            </button>
          </div>

          <p className="text-steel text-xs text-center mt-4">
            We do not sell or share your information. Calls from <span className="font-mono">1-314-314-0009</span>.
          </p>
          {error && <p className="text-danger-red text-sm mt-2 text-center">{error}</p>}
        </form>
      )}

      {submitted && read && (
        <>
          {callTriggered ? (
            <div className="bg-panel border border-command-amber/40 p-6 mb-8 text-center">
              <div className="font-mono text-sm text-command-amber uppercase tracking-[0.15em] mb-2">
                Expect a call, {name}
              </div>
              <p className="text-steel text-sm">
                Our AI agent will call {phone} from <span className="font-mono text-signal-white">1-314-314-0009</span> within
                a few minutes. Review your readiness report below in the meantime.
              </p>
            </div>
          ) : (
            <div className="bg-panel border border-grid-line p-6 mb-8 text-center">
              <div className="font-mono text-sm text-command-amber uppercase tracking-[0.15em] mb-2">
                We have your readiness report ready
              </div>
              <p className="text-steel text-sm">
                We could not trigger the call automatically just now. Use Start a Conversation below,
                or call us directly at <span className="font-mono text-signal-white">1-314-314-0009</span>.
              </p>
            </div>
          )}

          <div className="bg-panel border border-grid-line p-8 mb-8">
            <div className="font-mono text-sm text-command-amber uppercase tracking-[0.15em] mb-3">Your readiness report</div>
            <h3 className="text-2xl font-bold text-signal-white mb-6">{read.verdict}</h3>
            <ol className="space-y-4 mb-8">
              {read.observations.map((obs, i) => (
                <li key={i} className="flex gap-4 text-steel leading-relaxed">
                  <span className="font-mono text-command-amber">{['I.', 'II.', 'III.'][i]}</span>
                  <span>{obs}</span>
                </li>
              ))}
            </ol>

            <div className="border-t border-grid-line pt-6 mb-6">
              <div className="font-mono text-xs text-command-amber uppercase tracking-[0.15em] mb-3">Agenda for the call</div>
              <ul className="space-y-2 mb-0">
                {read.agenda.map((item, i) => (
                  <li key={i} className="text-signal-white text-sm flex gap-3">
                    <span className="font-mono text-command-amber">{i + 1}.</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-grid-line pt-6">
              <div className="font-mono text-xs text-command-amber uppercase tracking-[0.15em] mb-2">Recommended mode</div>
              <h4 className="text-xl text-signal-white mb-4">{read.recommendedMode}</h4>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={onContactClick}
                  className="btn btn-primary"
                >
                  {callTriggered ? "Call didn't connect? Start a conversation" : 'Start a conversation'}
                </button>
                {read.nextStepHref.startsWith('/') && (
                  <a
                    href={read.nextStepHref}
                    className="btn btn-secondary"
                  >
                    Or read what we'd suggest first
                  </a>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="text-center">
        <button
          onClick={onReset}
          className="font-mono text-xs text-steel uppercase tracking-[0.15em] hover:text-signal-white transition-colors"
        >
          ← Restart the diagnostic
        </button>
      </div>
    </article>
  );
};

export default DiagnosticResult;
